import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ValidationResults = ({ results, onExport, onSaveToHistory }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock results data if not provided
  const mockResults = [
    {
      id: 1,
      email: 'john.doe@example.com',
      status: 'valid',
      errorType: null,
      deliverabilityScore: 95,
      domain: 'example.com',
      mxRecord: true,
      disposable: false,
      freeProvider: false,
      processingTime: 0.12
    },
    {
      id: 2,
      email: 'invalid.email@nonexistent.xyz',
      status: 'invalid',
      errorType: 'Domain not found',
      deliverabilityScore: 0,
      domain: 'nonexistent.xyz',
      mxRecord: false,
      disposable: false,
      freeProvider: false,
      processingTime: 0.08
    },
    {
      id: 3,
      email: 'test@tempmail.com',
      status: 'risky',
      errorType: 'Disposable email',
      deliverabilityScore: 25,
      domain: 'tempmail.com',
      mxRecord: true,
      disposable: true,
      freeProvider: false,
      processingTime: 0.15
    },
    {
      id: 4,
      email: 'user@gmail.com',
      status: 'valid',
      errorType: null,
      deliverabilityScore: 88,
      domain: 'gmail.com',
      mxRecord: true,
      disposable: false,
      freeProvider: true,
      processingTime: 0.10
    },
    {
      id: 5,
      email: 'contact@business.org',
      status: 'unknown',
      errorType: 'Timeout',
      deliverabilityScore: 50,
      domain: 'business.org',
      mxRecord: true,
      disposable: false,
      freeProvider: false,
      processingTime: 2.00
    }
  ];

  const emailResults = results || mockResults;

  // Filter results based on status
  const filteredResults = useMemo(() => {
    if (filterStatus === 'all') return emailResults;
    return emailResults.filter(result => result.status === filterStatus);
  }, [emailResults, filterStatus]);

  // Sort results
  const sortedResults = useMemo(() => {
    if (!sortConfig.key) return filteredResults;

    return [...filteredResults].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredResults, sortConfig]);

  // Paginate results
  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedResults.slice(startIndex, startIndex + pageSize);
  }, [sortedResults, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedResults.length / pageSize);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = () => {
    if (selectedEmails.size === paginatedResults.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(paginatedResults.map(result => result.id)));
    }
  };

  const handleSelectEmail = (id) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedEmails(newSelected);
  };

  const getStatusBadge = (status) => {
    const badges = {
      valid: { color: 'text-success bg-success/10', icon: 'CheckCircle', text: 'Valid' },
      invalid: { color: 'text-error bg-error/10', icon: 'XCircle', text: 'Invalid' },
      risky: { color: 'text-warning bg-warning/10', icon: 'AlertTriangle', text: 'Risky' },
      unknown: { color: 'text-text-secondary bg-gray-100', icon: 'HelpCircle', text: 'Unknown' }
    };

    const badge = badges[status] || badges.unknown;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon name={badge.icon} size={12} className="mr-1" />
        {badge.text}
      </span>
    );
  };

  const getDeliverabilityColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    if (score >= 40) return 'text-error';
    return 'text-text-secondary';
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'ArrowUpDown';
    return sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const handleExport = (format) => {
    const selectedResults = selectedEmails.size > 0 
      ? emailResults.filter(result => selectedEmails.has(result.id))
      : emailResults;
    
    onExport(selectedResults, format);
  };

  const getStatusCounts = () => {
    const counts = emailResults.reduce((acc, result) => {
      acc[result.status] = (acc[result.status] || 0) + 1;
      return acc;
    }, {});
    
    return {
      all: emailResults.length,
      valid: counts.valid || 0,
      invalid: counts.invalid || 0,
      risky: counts.risky || 0,
      unknown: counts.unknown || 0
    };
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="space-y-6">
      {/* Results Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {Object.entries(statusCounts).map(([status, count]) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`p-3 rounded-lg border text-center transition-all duration-200 ${
              filterStatus === status
                ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50'
            }`}
          >
            <div className="text-lg font-semibold">{count.toLocaleString()}</div>
            <div className="text-xs capitalize">{status === 'all' ? 'Total' : status}</div>
          </button>
        ))}
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-surface border border-border rounded-lg">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-text-secondary">
            {selectedEmails.size > 0 
              ? `${selectedEmails.size} selected`
              : `${sortedResults.length} results`
            }
          </span>
          
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="text-sm border border-border rounded px-2 py-1"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            iconName="Download"
            iconPosition="left"
          >
            Export CSV
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('excel')}
            iconName="FileSpreadsheet"
            iconPosition="left"
          >
            Export Excel
          </Button>
          
          <Button
            variant="primary"
            size="sm"
            onClick={onSaveToHistory}
            iconName="Save"
            iconPosition="left"
          >
            Save to History
          </Button>
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedEmails.size === paginatedResults.length && paginatedResults.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-border"
                  />
                </th>
                
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('email')}
                    className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-primary"
                  >
                    <span>Email Address</span>
                    <Icon name={getSortIcon('email')} size={14} />
                  </button>
                </th>
                
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-primary"
                  >
                    <span>Status</span>
                    <Icon name={getSortIcon('status')} size={14} />
                  </button>
                </th>
                
                <th className="px-4 py-3 text-left">
                  <span className="text-sm font-medium text-text-primary">Error Type</span>
                </th>
                
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('deliverabilityScore')}
                    className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-primary"
                  >
                    <span>Score</span>
                    <Icon name={getSortIcon('deliverabilityScore')} size={14} />
                  </button>
                </th>
                
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('domain')}
                    className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-primary"
                  >
                    <span>Domain</span>
                    <Icon name={getSortIcon('domain')} size={14} />
                  </button>
                </th>
                
                <th className="px-4 py-3 text-left">
                  <span className="text-sm font-medium text-text-primary">Flags</span>
                </th>
              </tr>
            </thead>
            
            <tbody className="divide-y divide-border">
              {paginatedResults.map((result) => (
                <tr key={result.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedEmails.has(result.id)}
                      onChange={() => handleSelectEmail(result.id)}
                      className="rounded border-border"
                    />
                  </td>
                  
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-text-primary">
                      {result.email}
                    </div>
                  </td>
                  
                  <td className="px-4 py-3">
                    {getStatusBadge(result.status)}
                  </td>
                  
                  <td className="px-4 py-3">
                    <div className="text-sm text-text-secondary">
                      {result.errorType || '-'}
                    </div>
                  </td>
                  
                  <td className="px-4 py-3">
                    <div className={`text-sm font-medium ${getDeliverabilityColor(result.deliverabilityScore)}`}>
                      {result.deliverabilityScore}%
                    </div>
                  </td>
                  
                  <td className="px-4 py-3">
                    <div className="text-sm text-text-primary">{result.domain}</div>
                  </td>
                  
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5 min-h-[24px]">
                      {result.disposable && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                          <Icon name="Trash2" size={10} className="mr-1" />
                          Disposable
                        </span>
                      )}
                      {result.freeProvider && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20">
                          <Icon name="Globe" size={10} className="mr-1" />
                          Free
                        </span>
                      )}
                      {result.roleBased && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                          <Icon name="Users" size={10} className="mr-1" />
                          Role-based
                        </span>
                      )}
                      {result.catchAll && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          <Icon name="Mail" size={10} className="mr-1" />
                          Catch-all
                        </span>
                      )}
                      {result.isHoneypot && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                          <Icon name="Shield" size={10} className="mr-1" />
                          Honeypot
                        </span>
                      )}
                      {result.isPwned && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                          <Icon name="AlertTriangle" size={10} className="mr-1" />
                          Breached
                        </span>
                      )}
                      {!result.mxRecord && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-error/10 text-error border border-error/20">
                          <Icon name="X" size={10} className="mr-1" />
                          No MX
                        </span>
                      )}
                      {/* Show "No flags" indicator when no flags are present */}
                      {!result.disposable && !result.freeProvider && !result.roleBased && !result.catchAll && !result.isHoneypot && !result.isPwned && result.mxRecord && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                          <Icon name="CheckCircle" size={10} className="mr-1" />
                          Clean
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <div className="text-sm text-text-secondary">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, sortedResults.length)} of {sortedResults.length} results
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
                iconSize={16}
              >
                Previous
              </Button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm rounded ${
                        currentPage === page
                          ? 'bg-primary text-white' :'text-text-secondary hover:text-primary'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                iconName="ChevronRight"
                iconSize={16}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidationResults;